export type ImportType = 'quick' | 'detailed';

export type ColType = 'text' | 'number' | 'integer' | 'yesno' | 'url' | 'urls' | 'list' | 'enum';

export interface ColumnRule {
  name: string;
  required?: boolean;
  type?: ColType;
  maxLength?: number;
  /** enum: canonical value -> accepted words (lowercase). */
  values?: { [canonical: string]: string[] };
}

export interface ImportTypeConfig {
  label: string;
  description: string;
  templateUrl: string;
  templateUrlQuick?: string;
  templateUrlDetail?: string;
  maxRows: number;
  columns: ColumnRule[];
}

const YESNO_COLS = ['IsDiscountAble', 'IsTrackStock', 'AllowNegativeStock', 'IsTaxAble', 'IsOpenDepartment', 'SellOnline', 'IsAutoPo'];
const INT_COLS = ['MinStockLevel', 'MaxStockLevel', 'ReOrderLevel', 'MinOrderQuantity', 'MaxOrderQuantity'];

const COLUMNS: ColumnRule[] = [
  { name: 'ProductName', required: true, maxLength: 150 },
  { name: 'SKUCode', maxLength: 55 },
  { name: 'Barcode', maxLength: 55 },
  { name: 'AlternateSKUs', type: 'list' },
  { name: 'AlternateBarcodes', type: 'list' },
  { name: 'Department' }, { name: 'Manufacturer' }, { name: 'Category' }, { name: 'Vendor' }, { name: 'VendorPartNumber' },
  { name: 'Description', maxLength: 2000 },
  ...YESNO_COLS.map(name => ({ name, type: 'yesno' as ColType })),
  { name: 'SoldAs' }, { name: 'UOM' },
  { name: 'CostPrice', required: true, type: 'number' },
  { name: 'SalePriceA', required: true, type: 'number' },
  { name: 'SalePriceB', type: 'number' }, { name: 'SalePriceC', type: 'number' },
  { name: 'DescriptionURL', type: 'urls', maxLength: 500 },
  { name: 'ProductPictureURL', type: 'urls' },
  { name: 'StockInHand', type: 'number' },
  { name: 'AgeRestriction', type: 'enum', values: { None: ['none', 'no', '0'], '18': ['18', '18+', 'mustbe18'], '21': ['21', '21+', 'mustbe21'] } },
  { name: 'Height', type: 'number' }, { name: 'Length', type: 'number' }, { name: 'Width', type: 'number' }, { name: 'Weight', type: 'number' },
  { name: 'ShippingDimensionUOM', type: 'enum', values: {
    Inch: ['inch', 'inches', 'in'], Centimeter: ['centimeter', 'centimeters', 'cm'], Millimeter: ['millimeter', 'millimeters', 'mm'],
    Meter: ['meter', 'meters', 'm'], Yard: ['yard', 'yards', 'yd'] } },
  { name: 'ShippingWeightUOM', type: 'enum', values: {
    Pound: ['pound', 'pounds', 'lb', 'lbs'], Kilogram: ['kilogram', 'kilograms', 'kg'], Gram: ['gram', 'grams', 'g'], Ounce: ['ounce', 'ounces', 'oz'] } },
  ...INT_COLS.map(name => ({ name, type: 'integer' as ColType })),
  { name: 'Tags', type: 'list', maxLength: 1000 },
  { name: 'Has Variants', type: 'yesno' },
  { name: 'Variant Name' },
  { name: 'Variant Image', type: 'url' },
  ...[1, 2, 3, 4].reduce((a, n) => a.concat([
    { name: `Option ${n} Name`, maxLength: 100 }, { name: `Option ${n} Value`, maxLength: 100 }] as ColumnRule[]), [] as ColumnRule[])
];

const FILE_CFG: ImportTypeConfig = {
  label: 'Product Import',
  description: 'Simple products and products with variants (one row per variant).',
  templateUrl: 'https://cloudstoragels.blob.core.windows.net/commonfiles/Production/ImportProductsCSVTemplate.csv',
  templateUrlQuick: 'https://cloudstoragels.blob.core.windows.net/commonfiles/Production/Product-Import-Quick.csv',
  templateUrlDetail: 'https://cloudstoragels.blob.core.windows.net/commonfiles/Production/Product-Import-Format.csv',
  maxRows: 5000,
  columns: COLUMNS
};

const QUICK_CFG: ImportTypeConfig = {
  label: 'Quick Import',
  description: 'Name, description, category, prices, one picture link and online/offline.',
  templateUrl: FILE_CFG.templateUrlQuick,
  maxRows: 2000,
  columns: [
    { name: 'ProductName', required: true, maxLength: 256 },
    { name: 'Description' },
    { name: 'Category', maxLength: 55 },
    { name: 'SalePriceA', type: 'number' },
    { name: 'CostPrice', type: 'number' },
    { name: 'ProductPictureURL', type: 'urls' },
    { name: 'SellOnline' }
  ]
};

export const IMPORT_TYPES: { [key in ImportType]: ImportTypeConfig } = { quick: QUICK_CFG, detailed: FILE_CFG };

export interface ImportIssue {
  row: number; // 0 = file/header level
  product: string;
  column: string;
  value: string;
  message: string;
  fix: string;
}

export interface ProductIssueGroup { product: string; issues: ImportIssue[]; }

/** Minimal RFC4180 CSV parser (quotes, escaped quotes, CRLF). */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [], field = '', inQuotes = false;
  if (text.charCodeAt(0) === 0xFEFF) { text = text.slice(1); }
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } else { inQuotes = false; }
      } else { field += c; }
    } else if (c === '"') { inQuotes = true; }
    else if (c === ',') { row.push(field); field = ''; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') { i++; }
      row.push(field); field = '';
      rows.push(row); row = [];
    } else { field += c; }
  }
  if (field !== '' || row.length) { row.push(field); rows.push(row); }
  return rows;
}

const norm = (s: string) => (s || '').toLowerCase().replace(/[\s_]/g, '');
const YES = ['yes', 'y', 'true', 't', '1'];
const NO = ['no', 'n', 'false', 'f', '0'];
const NUM_RE = /^(\d+|\d{1,3}(,\d{3})+)(\.\d+)?$|^\.\d+$/;
const URL_RE = /^https?:\/\/\S+$/i;

export function validateCsv(text: string, cfg: ImportTypeConfig): { issues: ImportIssue[]; rowCount: number } {
  const issues: ImportIssue[] = [];
  const all = parseCsv(text);
  const fileIssue = (message: string, fix: string) => issues.push({ row: 0, product: 'File', column: '', value: '', message, fix });
  if (all.length === 0 || !all.some(r => r.some(v => v.trim() !== ''))) {
    fileIssue('The file is empty.', 'Use the template, add your products under the header row and save as CSV UTF-8.');
    return { issues, rowCount: 0 };
  }
  const idx: { [key: string]: number } = {};
  all[0].forEach((h, i) => { const k = norm(h); if (k && idx[k] === undefined) { idx[k] = i; } });
  if (cfg === FILE_CFG && !Object.keys(idx).some(k => QUICK_CFG.columns.every(c => norm(c.name) !== k))) {
    issues.push({ row: 1, product: 'File', column: 'Header row (line 1)', value: all[0].map(h => h.trim()).filter(Boolean).join(', '),
      message: 'These columns match the Quick template, but "Detailed import" is selected. Detailed import needs extra columns such as SKUCode, Barcode, Department and StockInHand.',
      fix: 'Click "Quick import" in step 1 and choose the file again, or download the Detail Template for a detailed import.' });
    return { issues, rowCount: all.length - 1 };
  }
  const missing = cfg.columns.filter(c => c.required && idx[norm(c.name)] === undefined);
  if (missing.length) {
    issues.push({ row: 1, product: 'File', column: 'Header row (line 1)', value: all[0].map(h => h.trim()).filter(Boolean).join(', '),
      message: 'Missing required column(s): ' + missing.map(c => c.name).join(', ') + '.',
      fix: 'Keep the header row (row 1) exactly as in the template and do not rename or delete columns.' });
    return { issues, rowCount: all.length - 1 };
  }

  // data rows with their real line numbers; blank rows are skipped
  const data = all.map((r, i) => ({ r, line: i + 1 })).slice(1).filter(x => x.r.some(v => v.trim() !== ''));
  if (data.length === 0) { fileIssue('The file has no product rows.', 'Add your products under the header row.'); }
  if (data.length > cfg.maxRows) {
    fileIssue(`The file has ${data.length} rows; the maximum is ${cfg.maxRows}.`, `Split the file into parts of ${cfg.maxRows} rows or fewer.`);
  }

  const get = (r: string[], col: string) => { const i = idx[norm(col)]; return i === undefined ? '' : (r[i] || '').trim(); };
  const hasVariantCol = idx[norm('Has Variants')] !== undefined;
  const optCols = [1, 2, 3, 4].map(n => ({ name: `Option ${n} Name`, value: `Option ${n} Value` }));

  let lastName = '';
  const variantGroups: { [name: string]: { names: string[]; combos: { [k: string]: number } } } = {};

  data.forEach(({ r, line }) => {
    const name = get(r, 'ProductName');
    const hv = get(r, 'Has Variants');
    const optionFilled = optCols.some(o => get(r, o.name) !== '' || get(r, o.value) !== '');
    const isVariant = hasVariantCol ? YES.indexOf(hv.toLowerCase()) >= 0 : optionFilled;
    if (name) { lastName = name; }
    const product = name || (isVariant && lastName ? lastName : `(no name, line ${line})`);
    const add = (column: string, value: string, message: string, fix: string) =>
      issues.push({ row: line, product, column, value, message, fix });

    cfg.columns.forEach(col => {
      const v = get(r, col.name);
      if (v === '') {
        if (!col.required) { return; }
        if (col.name === 'ProductName') {
          add(col.name, '', isVariant ? 'ProductName is empty but Has Variants is Yes.' : 'ProductName is missing.',
            isVariant ? 'Type the product name on every row of this product (copy it down).' : 'Enter the product name.');
        } else if (col.name === 'SalePriceA') {
          add(col.name, '', 'SalePriceA is missing.', 'Enter the selling price, e.g. 5.99.');
        } else {
          add(col.name, '', 'CostPrice is missing.', 'Enter what the product costs you, e.g. 3.50 (0 if unknown).');
        }
        return;
      }
      if (col.maxLength && v.length > col.maxLength) {
        add(col.name, v.length > 40 ? v.slice(0, 40) + '...' : v, `${col.name} is ${v.length} characters; the limit is ${col.maxLength}.`,
          `Shorten it to ${col.maxLength} characters or fewer.`);
      }
      switch (col.type) {
        case 'yesno':
          if (YES.indexOf(v.toLowerCase()) < 0 && NO.indexOf(v.toLowerCase()) < 0) {
            add(col.name, v, `${col.name} "${v}" is not valid.`, 'Please type Yes or No.');
          }
          break;
        case 'number':
        case 'integer': {
          const s = v.replace(/^\$/, '').replace(/\s/g, '');
          if (s.startsWith('-')) { add(col.name, v, `${col.name} "${v}" is negative.`, 'Negative numbers are not accepted. Enter 0 or more.'); break; }
          if (!NUM_RE.test(s)) { add(col.name, v, `${col.name} "${v}" is not a number.`, 'Enter a number such as 5.99 (dot for decimals; no words or % sign).'); break; }
          if (col.type === 'integer' && !Number.isInteger(Number(s.replace(/,/g, '')))) {
            add(col.name, v, `${col.name} "${v}" must be a whole number.`, 'Enter a whole number such as 10.');
          }
          break;
        }
        case 'url':
          if (!URL_RE.test(v)) { add(col.name, v, `${col.name} is not a valid web link.`, 'Use ONE link that starts with http:// or https://.'); }
          break;
        case 'urls':
          v.split(/[;|]|,(?=\s*https?:)/i).map(u => u.trim()).filter(Boolean).forEach(u => {
            if (!URL_RE.test(u)) { add(col.name, u, `${col.name} link "${u}" is not valid.`, 'Every link must start with http:// or https://. Separate several links with a comma, ; or |.'); }
          });
          break;
        case 'enum': {
          const key = v.toLowerCase().replace(/\s/g, '');
          if (!Object.keys(col.values).some(c => col.values[c].indexOf(key) >= 0)) {
            add(col.name, v, `${col.name} "${v}" is not recognized.`, 'Use one of: ' + Object.keys(col.values).join(', ') + '.');
          }
          break;
        }
      }
    });

    if (hasVariantCol && !isVariant && optionFilled) {
      add('Has Variants', hv, 'Option columns are filled but Has Variants is not Yes.',
        'Type Yes in Has Variants on every row of this product, or clear the Option columns for a simple product.');
    }
    if (!isVariant) { return; }

    const pairs: string[] = [], names: string[] = [];
    optCols.forEach(o => {
      const n = get(r, o.name), val = get(r, o.value);
      if (n && !val) { add(o.value, '', `${o.name} is "${n}" but ${o.value} is empty.`, `Enter the value, e.g. ${n} = Red.`); }
      else if (!n && val) { add(o.name, '', `${o.value} is "${val}" but ${o.name} is empty.`, 'Enter what varies, e.g. Color or Size.'); }
      else if (n && val) { names.push(n.toLowerCase()); pairs.push(`${n.toLowerCase()}=${val.toLowerCase()}`); }
    });
    if (names.length === 0) {
      add('Option 1 Name', '', 'Variant row has no options.', 'Fill Option 1 Name and Option 1 Value, e.g. Color / Red.');
      return;
    }
    if (!name) { return; }
    const g = variantGroups[name.toLowerCase()];
    if (!g) { variantGroups[name.toLowerCase()] = { names, combos: { [pairs.join('|')]: line } }; return; }
    if (g.names.join('|') !== names.join('|')) {
      add('Option Name', names.join(', '), `Option names differ from the other rows of "${name}" (expected ${g.names.join(', ')}).`,
        'Use the same option names, in the same order, on every row of the product.');
    }
    const ck = pairs.join('|');
    if (g.combos[ck]) {
      add('Option Value', pairs.map(p => p.replace('=', ' = ')).join(', '), `This variant is listed twice (also on line ${g.combos[ck]}).`, 'Remove the duplicate row.');
    } else { g.combos[ck] = line; }
  });
  return { issues, rowCount: data.length };
}

export function groupIssues(issues: ImportIssue[]): ProductIssueGroup[] {
  const map: { [p: string]: ProductIssueGroup } = {};
  const out: ProductIssueGroup[] = [];
  issues.forEach(i => {
    let g = map[i.product];
    if (!g) { g = map[i.product] = { product: i.product, issues: [] }; out.push(g); }
    g.issues.push(i);
  });
  return out;
}
